import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Flex,
  Text,
  Tooltip,
  Spinner,
  useTheme,
  useColorMode,
  Select,
  Input,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from "@chakra-ui/react";
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import {
  faArrowDown,
  faArrowUp,
  faChevronLeft,
  faChevronRight,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import { cqAPI } from "../../lib/Auth";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./CallReport.css";

function CallReport() {
  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRange, setSelectedRange] = useState("Today");
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });

  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateRange, setDateRange] = useState([
    moment().startOf("day").toDate(),
    moment().endOf("day").toDate(),
  ]);
  const [startDate, endDate] = dateRange;
  const [campaigns, setCampaigns] = useState([]);
  const [campaign, setCampaign] = useState("");
  const theme = useTheme();
  const { colorMode } = useColorMode();

  const updateUrlHash = (
    startDate,
    endDate,
    campaign,
    phoneNumber,
    selectedRange
  ) => {
    const queryParams = new URLSearchParams({
      startDate: startDate ? moment(startDate).format("YYYY-MM-DD") : "",
      endDate: endDate ? moment(endDate).format("YYYY-MM-DD") : "",
      campaign,
      phoneNumber,
      selectedRange,
    }).toString();
    window.location.hash = queryParams;
  };

  const sortedCalls = useMemo(() => {
    let sortableItems = [...calls];
    if (sortConfig !== null) {
      sortableItems.sort((a, b) => {
        const va =
          sortConfig.key == "created"
            ? new Date(a[sortConfig.key])
            : a[sortConfig.key];
        const vb =
          sortConfig.key == "created"
            ? new Date(b[sortConfig.key])
            : b[sortConfig.key];
        if (va < vb) {
          return sortConfig.direction === "ascending" ? -1 : 1;
        }
        if (va > vb) {
          return sortConfig.direction === "ascending" ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [calls, sortConfig]);

  const fetchCalls = async (params = {}) => {
    setIsLoading(true);

    try {
      // Use parameters if provided, otherwise fallback to component state
      const formattedStartDate = params.startDate
        ? moment(params.startDate).format("L")
        : moment(dateRange[0]).format("L");
      const formattedEndDate = params.endDate
        ? moment(params.endDate).format("L")
        : moment(dateRange[1]).format("L");
      const campaignParam = params.campaign || campaign;
      const phoneNumberParam = params.phoneNumber || phoneNumber;

      const response = await cqAPI.get(
        `/reports/calls?rangeBeg=${formattedStartDate}&rangeEnd=${formattedEndDate}&phone_number=${phoneNumberParam}&campaign=${campaignParam}`
      );

      setCalls(response.data.phonecalls);
      setCampaigns(response.data.campaigns);
    } catch (error) {
      console.error("Error fetching calls:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const handleCampaignSelection = (value) => {
    setCampaign(value);
  };

  const handleDateRangeSelection = (value) => {
    setSelectedRange(value);
    let start, end;
    switch (value) {
      case "Today":
        start = moment().startOf("day");
        end = moment().endOf("day");
        break;
      case "Yesterday":
        start = moment().subtract(1, "days").startOf("day");
        end = moment().subtract(1, "days").endOf("day");
        break;
      case "Last 7 days":
        start = moment().subtract(6, "days").startOf("day");
        end = moment().endOf("day");
        break;
      case "Last 4 weeks":
        start = moment().subtract(4, "weeks").startOf("day");
        end = moment().endOf("day");
        break;
      case "Month to Date":
        start = moment().startOf("month");
        end = moment().endOf("day");
        break;
      case "Quarter to date":
        start = moment().startOf("quarter");
        end = moment().endOf("day");
        break;
      case "Year to date":
        start = moment().startOf("year");
        end = moment().endOf("day");
        break;
      case "Custom":
      default:
        start = null;
        end = null;
    }
    setDateRange([start ? start.toDate() : null, end ? end.toDate() : null]);
  };

  const renderCustomHeader = ({
                                date,
                                decreaseMonth,
                                increaseMonth,
                                prevMonthButtonDisabled,
                                nextMonthButtonDisabled,
                                monthDate,
                              }) => (
    <Flex
      direction="column"
      width="100%"
      paddingLeft="20px"
      paddingRight="20px"
    >
      <Flex justifyContent="center" alignItems="center" mt={4}>
        <Box
          p={2}
          borderRadius="md"
          borderWidth="2px"
          borderColor="purple.500"
          mr={2}
        >
          {startDate ? startDate.toLocaleDateString() : "Start Date"}
        </Box>
        <Text mx={2}>→</Text>
        <Box p={2} borderRadius="md" borderWidth="2px" borderColor="purple.500">
          {endDate ? endDate.toLocaleDateString() : "End Date"}
        </Box>
      </Flex>
      <Flex
        justifyContent="space-between"
        alignItems="center"
        paddingTop="10px"
        paddingTop="10px"
      >
        <Box
          as="button"
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronLeft} />
        </Box>
        <Box>
          {date.toLocaleString("default", {
            month: "long",
            year: "numeric",
          })}
        </Box>
        <Box
          as="button"
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
        >
          <FontAwesomeIcon icon={faChevronRight} />
        </Box>
      </Flex>
    </Flex>
  );

  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.slice(1)); // Remove the '#' part
    const startDate = hashParams.get("startDate")
      ? moment(hashParams.get("startDate")).toDate()
      : moment().startOf("day").toDate();
    const endDate = hashParams.get("endDate")
      ? moment(hashParams.get("endDate")).toDate()
      : moment().endOf("day").toDate();
    const campaign = hashParams.get("campaign") || "";
    const phoneNumber = hashParams.get("phoneNumber") || "";
    const selectedRange = hashParams.get("selectedRange") || "Today";

    setDateRange([startDate, endDate]);
    setCampaign(campaign);
    setPhoneNumber(phoneNumber);
    setSelectedRange(selectedRange);

    fetchCalls({
      startDate,
      endDate,
      campaign,
      phoneNumber,
      selectedRange,
    });
  }, []);

  useEffect(() => {
    updateUrlHash(startDate, endDate, campaign, phoneNumber, selectedRange);
  }, [startDate, endDate, campaign, phoneNumber, selectedRange]);

  const navigateToCall = (callId) => {
    const newSearchParams = new URLSearchParams(window.location.search);
    navigate(`/reports/calls/${callId}?${newSearchParams.toString()}`);
  };

  if (isLoading) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        height="100vh"
        width="100%"
        position="relative"
      >
        <Spinner className="centered-spinner" color="blue.500" size="xl" />
      </Flex>
    );
  }

  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    // Regular expression to match valid phone number characters
    const regex = /^[0-9]*$/;
    if (value === "" || regex.test(value)) {
      setPhoneNumber(value);
    }
  };

  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  return (
    <Box p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justifyContent="flex-start" mb={4} wrap="wrap">
        <Flex align="center" mr={2} mb={2}>
          <Select
            placeholder="Select range"
            value={selectedRange}
            onChange={(e) => handleDateRangeSelection(e.target.value)}
            className="date-type"
          >
            <option value="Today">Today</option>
            <option value="Yesterday">Yesterday</option>
            <option value="Last 7 days">Last 7 days</option>
            <option value="Last 4 weeks">Last 4 weeks</option>
            <option value="Month to Date">Month to Date</option>
            <option value="Quarter to date">Quarter to date</option>
            <option value="Year to date">Year to date</option>
            <option value="Custom">Custom</option>
          </Select>
        </Flex>
        <Flex align="center" mr={2} mb={2}>
          <DatePicker
            className={`custom-datepicker ${
              colorMode === "light" ? "light-theme" : "dark-theme"
            }`}
            selectsRange={true}
            startDate={startDate}
            endDate={endDate}
            onChange={(update) => {
              setDateRange(update);
              setSelectedRange("Custom"); // Ensure custom range is selected when dates are manually changed
            }}
            isClearable={true}
            renderCustomHeader={renderCustomHeader}
          />
        </Flex>
        <Flex align="center" mr={2} mb={2}>
          <Select
            value={campaign}
            onChange={(e) => handleCampaignSelection(e.target.value)}
          >
            <option value="">Select Origin</option>
            {campaigns.map((campaign, index) => (
              <option key={campaign._id} value={campaign._id}>
                {campaign.name.substr(0, 30)}
              </option>
            ))}
          </Select>
        </Flex>
        <Flex align="center" mr={2} mb={2}>
          <Input
            placeholder="Phone number"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            width="150px"
            type="tel"
            pattern="^\+?[0-9]{1,3}?[-. ]?([0-9]{2,3}[-. ]?){2,3}[0-9]{3,4}$"
            title="Phone number must be in a valid format (international or local)"
          />
        </Flex>
        <Flex ml="auto" mb={2}>
          <Button
            leftIcon={<FontAwesomeIcon icon={faPhone} />}
            onClick={fetchCalls}
          >
            Show calls
          </Button>
        </Flex>
      </Flex>
      <Table variant="simple" size="sm">
        <Thead>
          <Tr>
            <Th onClick={() => requestSort("created")} cursor="pointer">
              Date
            </Th>
            <Th onClick={() => requestSort("patient_name")} cursor="pointer">
              Patient
            </Th>
            <Th onClick={() => requestSort("type")} cursor="pointer">
              Direction
            </Th>
            <Th onClick={() => requestSort("campaign")} cursor="pointer">
              Origin
            </Th>
            <Th onClick={() => requestSort("from")} cursor="pointer">
              Phone Number
            </Th>
            <Th onClick={() => requestSort("duration")} cursor="pointer">
              Duration
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {sortedCalls.map((call) => (
            <Tr
              key={call._id}
              _hover={{ bg: hoverBgColor, cursor: "pointer" }}
              onClick={() => navigateToCall(call._id)}
            >
              <Td>{call.created}</Td>
              <Td>{call.patient_name}</Td>
              <Td>
                <Tooltip
                  label={call.type === "Incoming" ? "Incoming" : "Outgoing"}
                  hasArrow
                >
                  <Box>
                    <FontAwesomeIcon
                      icon={call.type === "Incoming" ? faArrowDown : faArrowUp}
                    />
                  </Box>
                </Tooltip>
              </Td>
              <Td>
                {call.type === "Incoming"
                  ? call.campaign || "N/A"
                  : call.from_ext
                    ? `Extension: ${call.from_ext}`
                    : call?.is_campaign ? "Automated" : "N/A"}
              </Td>
              <Td>{call.type === "Incoming" ? call.from : call.to}</Td>
              <Td>{call.duration}</Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  );
}

export default CallReport;
